import React from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { ChevronIcon, StarIcon, TrendIcon } from "./icons";
import { RatingsPerDayAccordionProps, RatingsPerDayEntry } from "./types";

const RatingRow: React.FC<{
  value: number;
  amount?: number | string;
  errorThreshold?: number;
  onChange?: (value: string) => void;
}> = ({ value, amount, errorThreshold, onChange }) => (
  <div className={styles.ratingRow}>
    <span className={styles.ratingLabel}>
      <span>{value}</span>
      <StarIcon className={styles.ratingStar} />
    </span>
    <input
      type="text"
      inputMode="numeric"
      className={cs(styles.ratingInput, {
        [styles.ratingInputError]:
          errorThreshold !== undefined && Number(amount) >= errorThreshold,
      })}
      value={amount ?? ""}
      onChange={(e) => onChange?.(e.target.value)}
    />
  </div>
);

const DayCard: React.FC<{
  day: RatingsPerDayEntry;
  errorThreshold?: number;
  onRatingChange?: RatingsPerDayAccordionProps["onRatingChange"];
}> = ({ day, errorThreshold, onRatingChange }) => (
  <div className={styles.dayCard}>
    <div className={styles.dayCardHeader}>
      <span className={styles.dayCardDate}>{day.label}</span>
      <TrendIcon className={styles.trendIcon} />
    </div>
    <RatingRow
      value={4}
      amount={day.fourStarRating}
      errorThreshold={errorThreshold}
      onChange={(v) => onRatingChange?.(day.date, "fourStarRating", v)}
    />
    <RatingRow
      value={5}
      amount={day.fiveStarRating}
      errorThreshold={errorThreshold}
      onChange={(v) => onRatingChange?.(day.date, "fiveStarRating", v)}
    />
  </div>
);

/**
 * "Ratings per day" accordion step (Figma 566:12183). Collapsible section
 * header (title + live 4★/5★ totals) over a wrapping grid of per-day rating
 * cards. Pure presentational — day data, totals input and handlers all come
 * from props.
 */
export const RatingsPerDayAccordion: React.FC<RatingsPerDayAccordionProps> = ({
  title,
  expanded = true,
  onToggle,
  days,
  onRatingChange,
  errorThreshold,
  className,
}) => {
  const totals = days.reduce(
    (acc, day) => {
      acc.four += Number(day.fourStarRating) || 0;
      acc.five += Number(day.fiveStarRating) || 0;
      return acc;
    },
    { four: 0, five: 0 },
  );

  return (
    <div className={cs(styles.card, { [styles.active]: expanded }, className)}>
      <div className={styles.left}>
        <div className={styles.headerRow}>
          <div className={styles.titleGroup}>
            <button
              type="button"
              className={styles.toggle}
              onClick={onToggle}
              aria-expanded={expanded}
              aria-label={expanded ? "Collapse" : "Expand"}
            >
              <ChevronIcon
                className={cs(styles.chevron, {
                  [styles.chevronCollapsed]: !expanded,
                })}
              />
            </button>
            <span className={styles.title}>{title}</span>
          </div>

          <div className={styles.summary}>
            <span className={styles.summaryItem}>
              <span className={styles.summaryValueStar}>
                <span>4</span>
                <StarIcon className={styles.summaryStar} />
              </span>
              <span className={styles.summaryDash}>—</span>
              <span className={styles.summaryCount}>{totals.four}</span>
            </span>
            <span className={styles.summaryItem}>
              <span className={styles.summaryValueStar}>
                <span>5</span>
                <StarIcon className={styles.summaryStar} />
              </span>
              <span className={styles.summaryDash}>—</span>
              <span className={styles.summaryCount}>{totals.five}</span>
            </span>
          </div>
        </div>

        {expanded && (
          <div className={styles.grid}>
            {days.map((day) => (
              <DayCard
                    key={day.date}
                    day={day}
                    errorThreshold={errorThreshold}
                    onRatingChange={onRatingChange}
                  />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RatingsPerDayAccordion;
